'use client';

import React from 'react';
import Link from 'next/link';
import { useSite } from '@/context/SiteContext';
import {
  Shield, Database, Cookie, Clock, UserCheck,
  Lock, Baby, Globe, RefreshCw, Mail, ChevronRight,
} from 'lucide-react';

const SECTIONS = [
  { id: 'information',   num: 1,  icon: Database,    title: 'Information We Collect' },
  { id: 'use',           num: 2,  icon: UserCheck,   title: 'How We Use Your Information' },
  { id: 'providers',    num: 3,  icon: Globe,       title: 'Data Providers & Third Parties' },
  { id: 'cookies',      num: 4,  icon: Cookie,      title: 'Cookies & Tracking Technologies' },
  { id: 'retention',    num: 5,  icon: Clock,       title: 'Data Retention' },
  { id: 'rights',       num: 6,  icon: UserCheck,   title: 'Your Rights & Choices' },
  { id: 'security',     num: 7,  icon: Lock,        title: 'Data Security' },
  { id: 'children',     num: 8,  icon: Baby,        title: "Children's Privacy" },
  { id: 'international',num: 9,  icon: Globe,       title: 'International Users' },
  { id: 'changes',      num: 10, icon: RefreshCw,   title: 'Changes to This Policy' },
  { id: 'contact',      num: 11, icon: Mail,        title: 'Contact Us' },
];

const DATA_PROVIDERS = [
  { name: 'S&P Global Market Intelligence', detail: 'Fundamental & historical financial data' },
  { name: 'CBOE',                           detail: 'Real-time U.S. stock price data' },
  { name: 'Benzinga',                       detail: 'Analyst ratings & price targets' },
  { name: 'Finnhub',                        detail: 'Forecasts, ETF holdings, dividends' },
  { name: 'Nasdaq Data Link',               detail: 'Corporate actions (Sharadar SF1)' },
  { name: 'Financial Modeling Prep',        detail: 'Alternative download data' },
];

function SectionHeading({ id, num, icon: Icon, title }: { id: string; num: number; icon: React.ElementType; title: string }) {
  return (
    <div id={id} className="flex items-center gap-3 mb-4 scroll-mt-24">
      <div className="w-8 h-8 rounded-lg bg-sky-500/10 border border-sky-500/20 flex items-center justify-center flex-shrink-0">
        <Icon size={15} className="text-sky-400" />
      </div>
      <h2 className="text-base font-semibold text-white">
        <span className="text-sky-500 mr-2 font-mono text-sm">{num}.</span>{title}
      </h2>
    </div>
  );
}

function Prose({ children }: { children: React.ReactNode }) {
  return <div className="text-sm text-fg-muted leading-relaxed space-y-3">{children}</div>;
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-1.5 mt-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2 text-sm text-fg-muted">
          <ChevronRight size={13} className="text-sky-500 flex-shrink-0 mt-0.5" />
          {item}
        </li>
      ))}
    </ul>
  );
}

export default function PrivacyPage() {
  const { siteName } = useSite();
  return (
    <div className="min-h-screen bg-surface">

      {/* Hero */}
      <div className="border-b border-default bg-surface">
        <div className="max-w-5xl mx-auto px-2 py-14">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center">
              <Shield size={20} className="text-sky-400" />
            </div>
            <span className="text-xs font-semibold tracking-widest text-fg-subtle uppercase">Legal</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3 leading-tight">Privacy Policy</h1>
          <p className="text-fg-muted text-sm mb-4 max-w-2xl">
            This policy explains how {siteName} collects, uses, and protects your information when you use our website and services.
          </p>
          <div className="flex flex-wrap gap-4 text-xs text-fg-subtle">
            <span><span className="text-fg-muted font-medium">Effective date:</span> April 1, 2026</span>
            <span><span className="text-fg-muted font-medium">Last updated:</span> April 1, 2026</span>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-2 py-10 flex gap-10">

        {/* Sticky table of contents — hidden on mobile */}
        <aside className="hidden lg:block w-60 flex-shrink-0">
          <div className="sticky top-6 bg-card-bg border border-default rounded-xl p-4">
            <p className="text-xs font-semibold text-fg-subtle uppercase tracking-widest mb-3">Contents</p>
            <nav className="space-y-0.5">
              {SECTIONS.map(({ id, num, title }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs text-fg-muted hover:text-white hover:bg-card-hover-bg transition-colors"
                >
                  <span className="text-sky-500 font-mono w-4 text-right flex-shrink-0">{num}</span>
                  {title}
                </a>
              ))}
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0 space-y-8">

          {/* 1. Information we collect */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="information" num={1} icon={Database} title="Information We Collect" />
            <Prose>
              <p>We collect information in two ways: directly from you and automatically through your use of our site.</p>
              <div>
                <p className="text-slate-300 font-medium mb-1">Information you provide</p>
                <BulletList items={[
                  'Name and email address when you contact us via our contact form or email',
                  'Account credentials if you register for a user account',
                  'Messages, feedback, or data accuracy reports you submit to us',
                ]} />
              </div>
              <div>
                <p className="text-slate-300 font-medium mb-1">Information collected automatically</p>
                <BulletList items={[
                  'IP address and approximate geographic location',
                  'Browser type, device type, and operating system',
                  'Pages visited, time spent on pages, and referring URLs',
                  'Search queries entered on the site',
                  'Clickstream data and interaction events',
                ]} />
              </div>
              <p className="text-xs text-fg-subtle border-l-2 border-default pl-3">
                We do not collect sensitive financial data such as brokerage account numbers, Social Security numbers, or payment card information.
              </p>
            </Prose>
          </section>

          {/* 2. How we use your information */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="use" num={2} icon={UserCheck} title="How We Use Your Information" />
            <Prose>
              <p>We use the information we collect for the following purposes:</p>
              <BulletList items={[
                'To operate and improve the website, including resolving data inaccuracies you report',
                'To respond to your inquiries and support requests',
                'To personalize your experience and remember your preferences',
                'To analyze usage patterns and optimize site performance',
                'To send service-related communications (e.g., account updates), if applicable',
                'To detect and prevent fraud, abuse, or security incidents',
                'To comply with applicable legal obligations',
              ]} />
              <p className="text-xs text-fg-subtle border-l-2 border-sky-500/40 pl-3">
                We do not sell your personal information to third parties. We do not use your data to make automated decisions that produce legal or similarly significant effects.
              </p>
            </Prose>
          </section>

          {/* 3. Data providers */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="providers" num={3} icon={Globe} title="Data Providers & Third Parties" />
            <Prose>
              <p>Our site displays financial data sourced from enterprise data providers. These providers operate under their own privacy and data policies. The third parties we work with include:</p>
            </Prose>
            <div className="grid sm:grid-cols-2 gap-2 mt-4">
              {DATA_PROVIDERS.map(({ name, detail }) => (
                <div key={name} className="bg-surface border border-default rounded-xl px-4 py-3">
                  <p className="text-xs font-semibold text-slate-200 mb-0.5">{name}</p>
                  <p className="text-xs text-fg-subtle">{detail}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-fg-subtle mt-4 leading-relaxed">
              We may also use third-party analytics, advertising, and infrastructure services that process limited user data (such as anonymized usage statistics) on our behalf. These parties are contractually bound to use your data only for the purposes we specify.
            </p>
          </section>

          {/* 4. Cookies */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="cookies" num={4} icon={Cookie} title="Cookies & Tracking Technologies" />
            <Prose>
              <p>We use cookies and similar technologies to operate the site and understand how it is used. These include:</p>
              <div className="space-y-2 mt-1">
                {[
                  { label: 'Essential cookies', desc: 'Required for the site to function (e.g., session management, preferences)', color: 'bg-sky-500' },
                  { label: 'Analytics cookies', desc: 'Help us understand traffic patterns and user behavior in aggregate', color: 'bg-violet-500' },
                  { label: 'Advertising cookies', desc: 'May be set by third-party ad partners to serve relevant ads, if advertising is enabled', color: 'bg-amber-500' },
                ].map(({ label, desc, color }) => (
                  <div key={label} className="flex items-start gap-3 bg-surface border border-default rounded-xl px-4 py-3">
                    <span className={`w-2 h-2 rounded-full ${color} flex-shrink-0 mt-1.5`} />
                    <div>
                      <p className="text-xs font-semibold text-slate-200">{label}</p>
                      <p className="text-xs text-fg-subtle mt-0.5">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p>You can control cookie preferences through your browser settings or any cookie consent tool displayed on the site. Note that disabling certain cookies may affect site functionality.</p>
            </Prose>
          </section>

          {/* 5. Data retention */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="retention" num={5} icon={Clock} title="Data Retention" />
            <Prose>
              <p>We retain personal information only for as long as necessary to fulfill the purposes described in this policy, or as required by law. Specifically:</p>
              <BulletList items={[
                'Contact form submissions and support emails are retained for up to 3 years',
                'Account data is retained for the life of your account plus a reasonable wind-down period after deletion',
                'Anonymized analytics data may be retained indefinitely',
              ]} />
              <p>You may request deletion of your personal data at any time by contacting us (see Section 11).</p>
            </Prose>
          </section>

          {/* 6. Your rights */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="rights" num={6} icon={UserCheck} title="Your Rights & Choices" />
            <Prose>
              <p>Depending on your location, you may have the following rights regarding your personal data:</p>
              <div className="grid sm:grid-cols-2 gap-2 mt-1">
                {[
                  { right: 'Access', desc: 'Request a copy of the personal data we hold about you' },
                  { right: 'Correction', desc: 'Request that inaccurate or incomplete data be corrected' },
                  { right: 'Deletion', desc: 'Request that we delete your personal data' },
                  { right: 'Objection', desc: 'Object to certain types of processing, including direct marketing' },
                  { right: 'Portability', desc: 'Request your data in a structured, machine-readable format' },
                  { right: 'Withdrawal', desc: 'Where processing is based on consent, withdraw it at any time' },
                ].map(({ right, desc }) => (
                  <div key={right} className="bg-surface border border-default rounded-xl px-4 py-3">
                    <p className="text-xs font-semibold text-sky-400 mb-0.5">{right}</p>
                    <p className="text-xs text-fg-subtle">{desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-xs text-fg-subtle border-l-2 border-default pl-3">
                Residents of California (CCPA), the European Economic Area (GDPR), and other jurisdictions with applicable privacy laws may have additional rights. To exercise any of these rights, contact us at{' '}
                <a href="mailto:support@stocksiqpro.com" className="text-sky-400 hover:text-sky-300 underline">support@stocksiqpro.com</a>.
              </p>
            </Prose>
          </section>

          {/* 7. Security */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="security" num={7} icon={Lock} title="Data Security" />
            <Prose>
              <p>
                We implement industry-standard technical and organizational measures to protect your information against unauthorized access, disclosure, alteration, or destruction. These include encrypted data transmission (HTTPS), access controls, and regular security reviews.
              </p>
              <p className="text-xs text-fg-subtle border-l-2 border-amber-500/40 pl-3">
                No system is completely secure. If you believe your data has been compromised, please contact us immediately.
              </p>
            </Prose>
          </section>

          {/* 8. Children */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="children" num={8} icon={Baby} title="Children's Privacy" />
            <Prose>
              <p>
                Our site is not directed at children under the age of 13. We do not knowingly collect personal information from children. If we become aware that we have inadvertently collected data from a child, we will delete it promptly. If you believe a child has provided us with personal data, please contact us.
              </p>
            </Prose>
          </section>

          {/* 9. International users */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="international" num={9} icon={Globe} title="International Users" />
            <Prose>
              <p>
                {siteName} is operated from the United States. If you access our site from outside the U.S., your information may be transferred to and processed in the U.S., where privacy laws may differ from those in your country. By using our site, you acknowledge this transfer. Where required by law, we apply appropriate safeguards for cross-border data transfers.
              </p>
            </Prose>
          </section>

          {/* 10. Changes */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="changes" num={10} icon={RefreshCw} title="Changes to This Policy" />
            <Prose>
              <p>
                We may update this privacy policy from time to time to reflect changes in our practices, legal requirements, or site features. When we make material changes, we will update the &ldquo;Last updated&rdquo; date at the top of this page. We encourage you to review this policy periodically. Continued use of the site after changes are posted constitutes acceptance of the updated policy.
              </p>
            </Prose>
          </section>

          {/* 11. Contact */}
          <section className="bg-card-bg border border-default rounded-2xl p-6">
            <SectionHeading id="contact" num={11} icon={Mail} title="Contact Us" />
            <Prose>
              <p>If you have any questions, concerns, or requests regarding this privacy policy or the way we handle your data, please reach out to us:</p>
            </Prose>
            <div className="mt-4 flex flex-col sm:flex-row gap-3">
              <a
                href="mailto:support@stocksiqpro.com"
                className="flex items-center gap-2 px-4 py-3 bg-surface border border-default hover:border-sky-500/40 rounded-xl text-sm text-slate-300 hover:text-white transition-colors"
              >
                <Mail size={15} className="text-sky-400" />
                support@stocksiqpro.com
              </a>
              <Link
                href="/contact"
                className="flex items-center gap-2 px-4 py-3 bg-sky-500 hover:bg-sky-600 rounded-xl text-sm text-white font-medium transition-colors"
              >
                Contact Form <ChevronRight size={14} />
              </Link>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
}
